/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    Button,
    Modal,
    Drawer,
    ModalBody,
    DrawerBody,
    ModalContent,
    DrawerContent,
    ModalOverlay,
    DrawerOverlay,
    Stack,
    Text,
    Image,
    useDisclosure,
    Heading,
    Divider,
    useStyleConfig,
    Box,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerId,
    useCustomerType,
    useCustomerBaskets,
    useShopperBasketsMutation,
    useCustomer,
    useCommerceApi,
    useAccessToken
} from '@salesforce/commerce-sdk-react'
import LoginForm from '../components/login'
import ResetPasswordForm from '../components/reset-password'
import RegisterForm from '@salesforce/retail-react-app/app/components/register'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getUrl} from '../utils/cloudinary-utils'
import useResetPassword from './use-reset-password'
import useTagManager from './use-tag-manager'
import CustomApi from './use-custom-api'
import {getCustomerBaskets} from '@salesforce/commerce-sdk-react/hooks/ShopperCustomers/queryKeyHelpers'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useQueryClient} from '@tanstack/react-query'
import {getGlobaleOrderTrackUrl, useIsGlobaleOperated} from '../contexts/globale'

export const LOGIN_VIEW = 'login'
export const REGISTER_VIEW = 'register'
export const PASSWORD_VIEW = 'password'
export const EMAIL_VIEW = 'email'

const LOGIN_ERROR = defineMessage({
    defaultMessage: 'Sorry, this does not match our records. Check your spelling and try again',
    id: 'auth_modal.error.incorrect_email_or_password'
})

export const AuthModal = ({
    initialView = LOGIN_VIEW,
    onLoginSuccess = noop,
    onRegistrationSuccess = noop,
    isOpen,
    isCartItem = false,
    productid = '',
    onOpen,
    onClose,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const customapi = new CustomApi()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const {data: customer} = useCustomer({parameters: {customerId}}, {enabled: !!customerId})
    const {sendPasswordResetEmail} = useResetPassword()
    const styles = useStyleConfig('LoginStyles')
    const isMobile = useBreakpointValue({base: true, md: false})
    const {isRegistered, customerType} = useCustomerType()
    const prevAuthType = usePrevious(customerType)

    const navigate = useNavigation()
    const [currentView, setCurrentView] = useState(initialView)
    const form = useForm()
    const submittedEmail = useRef()
    const tagManager = useTagManager()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const register = useAuthHelper(AuthHelpers.Register)
    const {site} = useMultiSite()
    const queryClient = useQueryClient()

    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const transferBasket = useShopperBasketsMutation('transferBasket')

    const submitForm = async (data) => {
        form.clearErrors()

        const onLoginSuccess = () => {
            navigate('/account')
        }

        return {
            login: async (data) => {
                try {
                    const guestBasket = baskets?.baskets?.[0]
                    const hasBasketItem = guestBasket?.productItems?.length > 0
                    const shouldTransferBasket = hasBasketItem && prevAuthType === 'guest'

                    tagManager.formFieldChange('login-form', 'Form Submit Intent')
                    const {customer_id: newCustomerId} = await login.mutateAsync({
                        username: data.email,
                        password: data.password
                    })

                    if (newCustomerId) {
                        localStorage.setItem('isLoginSuccess', true)
                    }

                    if (shouldTransferBasket) {
                        const basketsQueryKey = getCustomerBaskets.queryKey({
                            organizationId:
                                api.shopperCustomers.clientConfig.parameters.organizationId,
                            customerId: newCustomerId,
                            siteId: site.id
                        })

                        // save the guest basket to the cache to avoid flash of logged in customer basket
                        queryClient.setQueryData(basketsQueryKey, {
                            baskets: [guestBasket],
                            total: 1
                        })
                        const transferredBasket = await transferBasket.mutateAsync({
                            headers: {
                                // This is not required since the request has no body
                                // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                                'Content-Type': 'application/json'
                            },
                            parameters: {
                                overrideExisting: true
                            }
                        })

                        // Optional: align cache with server (mutation usually does this)
                        queryClient.setQueryData(basketsQueryKey, {
                            baskets: [transferredBasket],
                            total: 1
                        })
                    }

                    if (isCartItem && productid) {
                        navigate(`/cart?pid=${productid}`)
                    }
                    const token = await getTokenWhenReady()
                    const response = await customapi.post(
                        token,
                        'orce-check-loyalty',
                        '/',
                        {},
                        proxy
                    )
                    if (!!response?.isOrceDown && !!response?.error) {
                        localStorage.setItem('ORCEError', true)
                    } else {
                        localStorage.setItem('ORCEError', false)
                    }
                } catch (error) {
                    const message = /Unauthorized/i.test(error.message)
                        ? formatMessage(LOGIN_ERROR)
                        : formatMessage(API_ERROR_MESSAGE)
                    form.setError('global', {type: 'manual', message})
                    tagManager.errorDisplayed('form validation', message, 'login form')
                }
            },
            register: async (data) => {
                try {
                    const body = {
                        customer: {
                            firstName: data.firstName,
                            lastName: data.lastName,
                            email: data.email,
                            login: data.email
                        },
                        password: data.password
                    }

                    await register.mutateAsync(body)
                    onLoginSuccess()
                } catch (error) {
                    form.setError('global', {
                        type: 'manual',
                        message: formatMessage(API_ERROR_MESSAGE)
                    })
                    tagManager.errorDisplayed(
                        'form validation',
                        formatMessage(API_ERROR_MESSAGE),
                        'registration form'
                    )
                }
            },
            password: async (data) => {
                try {
                    await sendPasswordResetEmail(data.email)
                } catch (error) {
                    form.setError('global', {
                        type: 'manual',
                        message: formatMessage(API_ERROR_MESSAGE)
                    })
                    tagManager.errorDisplayed(
                        'form validation',
                        formatMessage(API_ERROR_MESSAGE),
                        'reset password form'
                    )
                    throw error
                }
            }
        }[currentView](data)
    }

    // Reset form and local state when opening the modal
    useEffect(() => {
        if (isOpen) {
            setCurrentView(initialView)
            submittedEmail.current = undefined
            form.reset()
        }
    }, [isOpen])

    // Auto-focus the first field in each form view
    useEffect(() => {
        const initialField = {
            [LOGIN_VIEW]: 'email',
            [REGISTER_VIEW]: 'firstName',
            [PASSWORD_VIEW]: 'email'
        }[currentView]
        const fieldsRef = form.control?.fieldsRef?.current
        fieldsRef?.[initialField]?.ref.focus()
    }, [form.control?.fieldsRef?.current])

    // Clear form state when changing views
    useEffect(() => {
        form.reset()
    }, [currentView])

    const [showSignedInBtn, setShowSignedInBtn] = useState(false)
    useEffect(() => {
        const isLoginSuccess = localStorage.getItem('isLoginSuccess') === 'true'

        if (isLoginSuccess && customer) {
            tagManager.loginSuccess(customer)
            localStorage.setItem('isLoginSuccess', false)
        }
        // Lets determine if the user has either logged in, or registed.
        const loggingIn = currentView === LOGIN_VIEW
        const registering = currentView === REGISTER_VIEW
        const isNowRegistered = isOpen && isRegistered && (loggingIn || registering)

        // If the customer changed, but it's not because they logged in or registered. Do nothing.
        if (!isNowRegistered) {
            return
        }

        // Show a toast only for those registed users returning to the site.
        if (loggingIn) {
            // Execute action to be performed on successful login
            onLoginSuccess()
            setShowSignedInBtn(true)
            window.setTimeout(() => {
                setShowSignedInBtn(false)
                // We are done with the modal.
                onClose()
            }, 1000)
        }

        if (registering) {
            // Execute action to be performed on successful registration
            onRegistrationSuccess()
        }
    }, [isRegistered, customer])

    const onBackToSignInClick = () =>
        initialView === PASSWORD_VIEW ? onClose() : setCurrentView(LOGIN_VIEW)

    const PasswordResetSuccess = () => (
        <Stack justify="center" align="center" spacing={6}>
            <Stack spacing="10px">
                <Heading {...styles.modal.heading}>
                    <FormattedMessage
                        defaultMessage="Request to Reset Your Password Received"
                        id="request.to.reset.password.title"
                    />
                </Heading>
                <Divider {...styles.modal.divider} />
                <Box {...styles.modal.descriptionBox}>
                    <Text {...styles.modal.descriptionText}>
                        <FormattedMessage
                            defaultMessage="Thanks for submitting your email address. If your email is registered to an account, we have sent you an email with the information needed to reset your password. If you do not receive an email within the next 12 hours then the email that you have provided is likely not registered with a KendraScott.com account. Please try a different email or create a new account. Please do not forget to check your spam if you do not see the email in your inbox."
                            id="request.to.reset.password.description"
                        />
                    </Text>
                </Box>
                <Button onClick={onBackToSignInClick} {...styles.modal.button}>
                    <FormattedMessage
                        defaultMessage="Back to Sign In"
                        id="auth_modal.password_reset_success.button.back_to_sign_in"
                    />
                </Button>
            </Stack>
        </Stack>
    )
    const initialFocusRef = useRef(null)
    return (
        <>
            <Modal
                size="lg"
                closeOnOverlayClick={true}
                data-testid="sf-auth-modal"
                isOpen={isOpen && !isMobile}
                onOpen={onOpen}
                onClose={onClose}
                initialFocusRef={initialFocusRef}
                {...props}
            >
                <ModalOverlay />
                <ModalContent borderRadius="20px">
                    <Box
                        top="10px"
                        right="10px"
                        position="absolute"
                        borderRadius="50%"
                        _hover={{
                            backgroundColor: 'gray.100'
                        }}
                        onClick={onClose}
                        padding="15px"
                        cursor="pointer"
                        transition="all, 0.3s ease"
                    >
                        <Image
                            {...styles.modal.crossImage}
                            alt="Cross Icon"
                            cursor="pointer"
                            src={getUrl('UIImages/images/crossed-icon.svg')}
                        />
                    </Box>
                    <ModalBody {...styles.modal.body}>
                        <div ref={initialFocusRef} display="none" tabIndex="-1" />{' '}
                        {/* Dummy focus element */}
                        {currentView != PASSWORD_VIEW && (
                            <LoginForm
                                form={form}
                                showSignedInBtn={showSignedInBtn}
                                submitForm={submitForm}
                                clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                                clickForgotPassword={() => setCurrentView(PASSWORD_VIEW)}
                            />
                        )}
                        {!form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                            <ResetPasswordForm
                                form={form}
                                submitForm={submitForm}
                                clickSignIn={onBackToSignInClick}
                            />
                        )}
                        {form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                            <PasswordResetSuccess />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Drawer
                data-testid="sf-auth-modal"
                isOpen={isOpen && isMobile}
                onOpen={onOpen}
                onClose={onClose}
                initialFocusRef={initialFocusRef}
                placement="bottom"
                size="md"
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={false}
                // Add the following 1 line to fix the Safari behavior of Drawer Close
                returnFocusOnClose={false}
                {...props}
            >
                <DrawerOverlay />
                <DrawerContent borderRadius="20px 20px 0 0">
                    <Box
                        top="10px"
                        right="10px"
                        position="absolute"
                        borderRadius="50%"
                        _hover={{
                            backgroundColor: 'gray.100'
                        }}
                        onClick={onClose}
                        padding="15px"
                        cursor="pointer"
                        transition="all, 0.3s ease"
                    >
                        <Image
                            {...styles.modal.crossImage}
                            alt="Cross Icon"
                            cursor="pointer"
                            src={getUrl('UIImages/images/crossed-icon.svg')}
                        />
                    </Box>
                    <DrawerBody {...styles.modal.body}>
                        <div ref={initialFocusRef} display="none" tabIndex="-1" />{' '}
                        {/* Dummy focus element */}
                        {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                            <LoginForm
                                form={form}
                                submitForm={submitForm}
                                clickCreateAccount={() => setCurrentView(REGISTER_VIEW)}
                                clickForgotPassword={() => setCurrentView(PASSWORD_VIEW)}
                            />
                        )}
                        {!form.formState.isSubmitSuccessful && currentView === REGISTER_VIEW && (
                            <RegisterForm
                                form={form}
                                submitForm={submitForm}
                                clickSignIn={onBackToSignInClick}
                            />
                        )}
                        {!form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                            <ResetPasswordForm
                                form={form}
                                submitForm={submitForm}
                                clickSignIn={onBackToSignInClick}
                            />
                        )}
                        {form.formState.isSubmitSuccessful && currentView === PASSWORD_VIEW && (
                            <PasswordResetSuccess />
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

AuthModal.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, REGISTER_VIEW, PASSWORD_VIEW]),
    isOpen: PropTypes.bool.isRequired,
    isCartItem: PropTypes.bool,
    productid: PropTypes.any,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    onRegistrationSuccess: PropTypes.func
}

/**
 *
 * @param {('register'|'login'|'password')} initialView - the initial view for the modal
 * @returns {Object} - Object props to be spread on to the AuthModal component
 */
export const useAuthModal = (initialView = LOGIN_VIEW) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const isGlobaleOperated = useIsGlobaleOperated()
    const orderTrackUrl = getGlobaleOrderTrackUrl()

    const handleOpen = useCallback(() => {
        if (isGlobaleOperated && orderTrackUrl) {
            window.location.assign(orderTrackUrl)
            return
        }
        onOpen()
    }, [isGlobaleOperated, orderTrackUrl, onOpen])

    return {
        initialView,
        isOpen,
        onOpen: handleOpen,
        onClose
    }
}
